import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from '@tanstack/react-router';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Scrollbar } from '../../../../components/atoms/scrollbar';
import { Destination } from '../../api/types/destination';
import { DestinationKioskStatusChip } from '../atoms/destination-kiosk-status-chip';

import TableActionCell from '@/components/atoms/table-action-cell';
import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { useOrganizationKiosks } from '@/features/shared/hooks/query/use-organization-kiosks';

export interface DestinationActionSlotPayload {
  id: number;
  name: string;
  accessControlProfilesIds: number[];
  destinationHasAvailableKiosks: boolean;
}

interface Props {
  data: Destination[];
  error: string;
  isLoading: boolean;
  actionsSlot: (payload: DestinationActionSlotPayload) => ReactNode;
}

export const DestinationsTable = ({ data, error, isLoading, actionsSlot }: Props) => {
  const { t } = useTranslation(['destinations']);
  const { data: organizationKiosks } = useOrganizationKiosks();
  const hasOrganizationKiosk = organizationKiosks && organizationKiosks.length > 0;

  const columns = [
    {
      id: 'name',
      label: t('destinations:table.name'),
    },
    {
      id: 'floor',
      label: t('destinations:table.floor'),
    },
    {
      id: 'maxCapacity',
      label: t('destinations:table.maxCapacity'),
    },
    {
      id: 'description',
      label: t('destinations:table.description'),
    },
    {
      id: 'acs',
      label: t('destinations:table.acs'),
    },
    ...(hasOrganizationKiosk
      ? [
          {
            id: 'kiosk',
            label: t('destinations:table.kiosk'),
          },
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.id}>{column.label}</TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(destination => (
              <TableRow
                hover
                key={destination.id}
                component={RouterLink}
                to={`/destinations/${destination.id}`}
                sx={{ textDecoration: 'none' }}>
                <TableCell>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <Typography color="inherit" variant="subtitle2">
                      {destination.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{destination.floor}</TableCell>
                <TableCell>{destination.maxCapacity}</TableCell>
                <TableCell>{destination.description}</TableCell>
                <TableCell>
                  {destination.accessControlProfiles.length > 0 ? (
                    <Tooltip
                      title={destination.accessControlProfiles.map(acs => (
                        <Box key={acs.id}>
                          {acs.name}
                          <br />
                        </Box>
                      ))}
                      placement="top">
                      {/* <Box sx={{ display: "flex" }}>
                        {destination.accessControlProfiles.map((acs) => {
                          return (
                            <Avatar key={acs.name} alt={acs.name} src={acs.name.slice(0, 2)} />
                          );
                        })}
                      </Box> */}
                      <Chip
                        variant="outlined"
                        color="primary"
                        label={`${destination.accessControlProfiles.length} ACP`}
                      />
                    </Tooltip>
                  ) : (
                    <Chip variant="outlined" size="small" color="error" label="N/A" />
                  )}
                </TableCell>
                {hasOrganizationKiosk && (
                  <TableCell>
                    <DestinationKioskStatusChip
                      hasAvailableKiosks={destination.allowedKioskIds.length > 0}
                      hostConfirmationObligatory={destination.hostConfirmationObligatory}
                      hostObligatory={destination.hostObligatory}
                    />
                  </TableCell>
                )}
                <TableActionCell>
                  {actionsSlot({
                    id: destination.id,
                    name: destination.name,
                    accessControlProfilesIds: destination.accessControlProfiles?.map(acs => acs.id),
                    destinationHasAvailableKiosks: destination.allowedKioskIds.length > 0,
                  })}
                </TableActionCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
      <DataStatusIndicator dataLength={data?.length} isFetching={isLoading} error={error} />
    </Box>
  );
};
